import jwtDecode from 'jwt-decode';
import { useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Collapse from '@mui/material/Collapse';
import Tooltip from '@mui/material/Tooltip';
import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined';

import { RootState } from 'store';
import { checkAccess, checkAccessByAdminRightLevel } from 'routes/PrivateRoute';
import { SidebarItemsProps } from './SidebarItems';
import { ROLES } from 'constant/PermissonConstant';
import { RoleType } from 'store/reducer/authReducer';

export function isAdminHasAccess(
  requiredAdminAccessLevel: number | undefined,
  currentUserRole: RoleType | '',
  decodeToken: any
) {
  return (
    currentUserRole === ROLES.ADMIN &&
    // @ts-ignore
    checkAccessByAdminRightLevel(requiredAdminAccessLevel, decodeToken?.adminRightLevel || 0)
  );
}

const ChildMenuItem = ({
  childItem,
  nested,
  isActive,
  t,
}: {
  childItem: any;
  nested: boolean;
  isActive: boolean;
  t: (key: string) => string;
}) => {
  const textRef = useRef<HTMLDivElement>(null);
  const [tooltipText, setTooltipText] = useState('');

  const handleMouseEnter = () => {
    const el = textRef.current?.querySelector('.MuiTypography-root') as HTMLElement | null;
    if (el && el.scrollWidth > el.clientWidth) {
      setTooltipText(t(childItem.title));
    }
  };

  return (
    <Tooltip title={tooltipText} placement='right' arrow disableInteractive>
      <ListItemButton
        {...(childItem.path ? { component: Link, to: childItem.path } : {})}
        sx={{
          pl: nested ? 12 : 10,
          pr: 2,
          py: 0.6,
          my: 0.2,
          borderRight: isActive ? '3px solid #00B3DC' : '3px solid transparent',
          backgroundColor: isActive ? 'rgba(0, 179, 220, 0.08)' : 'transparent',
          transition: 'all 0.15s ease',
          '&:hover': {
            backgroundColor: isActive ? 'rgba(0, 179, 220, 0.12)' : 'rgba(23, 26, 49, 0.5)',
            transform: 'translateX(2px)',
          },
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={() => setTooltipText('')}
      >
        <ListItemText
          ref={textRef}
          primary={t(childItem.title)}
          primaryTypographyProps={{
            fontSize: 12,
            fontWeight: isActive ? 500 : 400,
            color: isActive ? '#00B3DC' : '#fff',
            letterSpacing: 0.3,
            lineHeight: 1.4,
            noWrap: true,
          }}
        />
      </ListItemButton>
    </Tooltip>
  );
};

export const MultiLevelMenu = ({ item, nested }: { item: SidebarItemsProps; nested: boolean }) => {
  const { threadUserAsRole, accessToken } = useSelector((state: RootState) => state.auth);
  const { pathname } = useLocation();
  const decodeToken = accessToken && (jwtDecode(accessToken) as { adminRightLevel: number });
  const { t } = useTranslation();
  const { child } = item;
  const [open, setOpen] = useState(false);
  const textRef = useRef<HTMLDivElement>(null);
  const [tooltipText, setTooltipText] = useState('');

  if (!checkAccess(item.allowedRoles, threadUserAsRole)) return null;

  const showParentMenu = child?.some((childItem) => {
    if (childItem.accessAdminRightLevel) {
      return isAdminHasAccess(childItem.accessAdminRightLevel, threadUserAsRole, decodeToken);
    }
    return true;
  });

  if (!showParentMenu) return null;

  const handleClick = () => {
    setOpen((prev) => !prev);
  };

  const isParentActive = pathname.includes(item.parentPath || '') && !nested;
  const isExpanded = pathname.includes(item.parentPath || '') || open;

  const handleMouseEnter = () => {
    const el = textRef.current?.querySelector('.MuiTypography-root') as HTMLElement | null;
    if (el && el.scrollWidth > el.clientWidth) {
      setTooltipText(t(item.title));
    }
  };

  return (
    <>
      <Tooltip title={tooltipText} placement='right' arrow disableInteractive>
        <ListItemButton
          onClick={handleClick}
          sx={{
            mx: nested ? 0 : 1,
            my: 0.3,
            pl: nested ? 10 : 2,
            borderRadius: nested || isParentActive ? 0 : '8px',
            borderLeft: nested ? 'none' : isParentActive ? '3px solid #00B3DC' : '3px solid transparent',
            borderRight: nested && isParentActive ? '3px solid #00B3DC' : 'none',
            backgroundColor: isParentActive ? 'rgba(0, 179, 220, 0.12)' : 'transparent',
            transition: 'all 0.15s ease',
            py: 1,
            '&:hover': {
              backgroundColor: isParentActive ? 'rgba(0, 179, 220, 0.18)' : 'rgba(23, 26, 49, 0.7)',
            },
          }}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={() => setTooltipText('')}
        >
          {!nested && <ListItemIcon sx={{ minWidth: 36 }}>{item.icon}</ListItemIcon>}
          {nested && item.icon && <ListItemIcon sx={{ minWidth: 28 }}>{item.icon}</ListItemIcon>}
          <ListItemText
            ref={textRef}
            primary={t(item.title)}
            primaryTypographyProps={{
              fontSize: nested ? 12 : 13,
              fontWeight: nested ? 400 : 600,
              color: isParentActive ? '#00B3DC' : '#fff',
              letterSpacing: 0.3,
              lineHeight: 1.4,
              noWrap: true,
              marginLeft: 0,
            }}
          />
          <ExpandMoreOutlinedIcon
            sx={{
              color: isParentActive ? '#00B3DC' : 'rgba(255, 255, 255, 0.7)',
              fontSize: 18,
              transition: 'transform 0.25s ease',
              transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
          />
        </ListItemButton>
      </Tooltip>
      <Collapse in={isExpanded} timeout='auto' unmountOnExit>
        <List component='div' sx={{ backgroundColor: '#1C203C' }}>
          {child?.map((childItem, key) => {
            if (childItem.child?.length > 0) {
              // @ts-ignore
              return <MultiLevelMenu item={childItem} nested key={key} />;
            }

            if (
              childItem.accessAdminRightLevel === undefined ||
              isAdminHasAccess(childItem.accessAdminRightLevel, threadUserAsRole, decodeToken)
            ) {
              return (
                <ChildMenuItem
                  key={key}
                  childItem={childItem}
                  nested={nested}
                  isActive={!!childItem.path && pathname.startsWith(childItem.path)}
                  t={t}
                />
              );
            }
            return null;
          })}
        </List>
      </Collapse>
    </>
  );
};
